import React, { ChangeEvent, FocusEvent, useCallback, useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'
import { EventOrValueHandler } from 'redux-form'

import { NEUTRAL_COLOURS } from 'constants/colors'
import { FeatureFlags } from 'constants/security'
import { AI_PROMPT_TYPE, AiConversationBody } from 'services/ai/constants'

import auth from 'utils/auth'
import { getBrandingColor } from 'utils/branding'
import eventBus from 'utils/eventBus'

import { withAiService, withAiServiceProps } from 'services/ai'
import nurseryContextSelectors from 'services/nurseryContext/selectors'

import {
  Button,
  DropdownMenu,
  FieldError,
  Flex,
  LoadingDots,
  MaxCharactersText,
  Space,
  TextAreaField,
  Typography,
} from 'components'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import {
  StyledAiIcon,
  StyledAutosizeTextArea,
  StyledButtonContainer,
  StyledFooterContainer,
  StyledInputContainer,
} from './AiEditorStyled'
import TypingAnimation from './components/TypingAnimation'

const mapState = (state: RootState) => ({
  aiFlagIsEnabled: auth.SELECTORS.getIsAuthorised(state, { flags: [FeatureFlags.AI] }),
  nurseryAiSettings: nurseryContextSelectors.getNurseryAiSettings(state),
})

const connector = connect(mapState, null, null, { forwardRef: true })

type PropsFromRedux = ConnectedProps<typeof connector>

export const FORCE_APPLY_CHANGES = 'EVENT/FORCE_APPLY_CHANGES'
export const FORCE_CANCEL_CHANGES = 'EVENT/FORCE_CANCEL_CHANGES'

export interface AiEditorProps {
  aiPromptTypes?: Record<string, string>
  childName?: string
  disableFieldError?: boolean
  disabled?: boolean
  error?: string
  maxCharacters?: number
  onBlur?: (event: EventOrValueHandler<FocusEvent<any, Element>> | string) => void
  onChange?: (event: EventOrValueHandler<ChangeEvent<any>>) => void
  onEnable?: (value: boolean) => void
  placeholder?: string
  valid?: boolean
  value?: string
}

type AiEditorFullProps = AiEditorProps
  & PropsFromRedux
  & withAiServiceProps

const AiEditor: React.FC<AiEditorFullProps> = ({
  aiActions,
  aiFlagIsEnabled,
  aiPromptTypes = AI_PROMPT_TYPE,
  childName,
  disableFieldError,
  disabled,
  error,
  maxCharacters,
  nurseryAiSettings,
  onBlur,
  onChange,
  onEnable,
  placeholder,
  valid,
  value,
  ...rest
}) => {
  const [response, setResponse] = useState<string | null>(null)
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [typeOfImprovement, setTypeOfImprovement] = useState<string | null>(null)

  const handleImprove = async (type) => {
    setTypeOfImprovement(type)
    setIsLoading(true)
    setIsEnabled(true)
    onEnable?.(true)

    const body: AiConversationBody = {
      message: value,
      prompt: type,
    }

    if (childName) {
      body.childFirstName = childName
    }

    const { message } = await aiActions.getAiConversation({ body }) as any

    setResponse(message)
    setIsLoading(false)
    setIsTyping(true)
  }

  const handleRetry = () => {
    if (isLoading) return

    handleImprove(typeOfImprovement)
  }

  const handleCancel = useCallback(() => {
    setIsEnabled(false)
    onEnable?.(false)
    setIsLoading(false)
    setResponse(null)
    setTypeOfImprovement(null)
  }, [onEnable])

  const handleApply = useCallback(() => {
    if (isLoading) return

    onBlur?.(response)
    setIsEnabled(false)
    onEnable?.(false)
    setResponse(null)
    setTypeOfImprovement(null)
  }, [isLoading, onBlur, response, onEnable])

  useEffect(() => {
    eventBus.on(FORCE_APPLY_CHANGES, handleApply)
    eventBus.on(FORCE_CANCEL_CHANGES, handleCancel)

    return () => {
      eventBus.remove(FORCE_APPLY_CHANGES, handleApply)
      eventBus.remove(FORCE_CANCEL_CHANGES, handleCancel)
    }
  }, [response, isLoading])

  const renderAiResponse = () => {
    if (!isEnabled) return null

    if (isLoading) {
      return (
        <Flex alignItems="center" gap="4px">
          <Typography color={getBrandingColor('primary-color-lighten-10')} fontSize={14} variant="span" bold>
            {i18n.t('components:AiEditor:aiIsWriting')}
          </Typography>
          <LoadingDots color={getBrandingColor('primary-color-lighten-10')} />
        </Flex>
      )
    }

    if (isTyping) {
      return (
        <Typography color={NEUTRAL_COLOURS.BASIC} fontSize={14} lineHeight={20}>
          {response && <TypingAnimation text={response} onFinish={() => setIsTyping(false)} />}
        </Typography>
      )
    }

    return (
      <Typography color={NEUTRAL_COLOURS.BASIC} fontSize={14} lineHeight={20} whiteSpace="pre-wrap">
        {response}
      </Typography>
    )
  }

  if (!aiFlagIsEnabled || !nurseryAiSettings?.enabled) {
    return (
      <TextAreaField
        disableFieldError={disableFieldError}
        disabled={disabled}
        error={error}
        maxCharacters={maxCharacters}
        placeholder={placeholder}
        valid={valid}
        value={value}
        onBlur={onBlur as any}
        onChange={onChange as any}
        {...rest}
      />
    )
  }

  return (
    <React.Fragment>
      <StyledInputContainer>
        <StyledAutosizeTextArea
          $disabled={disabled}
          $hidden={isEnabled}
          $valid={valid}
          disabled={disabled}
          maxLength={maxCharacters}
          placeholder={placeholder}
          value={value}
          onBlur={onBlur as any}
          onChange={onChange as any}
          onHeightChange={(height: number) => height + 40}
          {...rest}
        />
      </StyledInputContainer>
      <StyledFooterContainer $isEnabled={isEnabled} $valid={valid}>
        {renderAiResponse()}
        <Space space="20px" />
        <StyledButtonContainer $isEnabled={isEnabled}>
          <Flex alignItems="center" gap="16px" width="100%">
            <Flex alignItems="center" gap="16px">
              <StyledAiIcon>
                <Button
                  disabled={!value || isLoading}
                  hierarchy="tertiary"
                  icon="ai"
                  label={i18n.t('components:AiEditor:improveWriting')}
                  margin="0"
                  size="small"
                  onClick={() => handleImprove(aiPromptTypes.IMPROVE_WRITING)}
                />
              </StyledAiIcon>
              <DropdownMenu disabled={!value || isLoading} placement="top-start" small>
                <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={12} padding="12px 0 10px 15px" transform="uppercase">
                  {i18n.t('components:AiEditor:adjustTone')}
                </Typography>
                <DropdownMenu.Item
                  icon="friendly"
                  label={i18n.t('components:AiEditor:friendly')}
                  onClick={() => handleImprove(aiPromptTypes.FRIENDLY)}
                />
                <DropdownMenu.Item
                  icon="professional"
                  label={i18n.t('components:AiEditor:professional')}
                  onClick={() => handleImprove(aiPromptTypes.PROFESSIONAL)}
                />
                <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={12} padding="15px 0 10px 15px" transform="uppercase">
                  {i18n.t('components:AiEditor:refineText')}
                </Typography>
                <DropdownMenu.Item
                  icon="shorten"
                  label={i18n.t('components:AiEditor:shorten')}
                  onClick={() => handleImprove(aiPromptTypes.SHORTEN)}
                />
                <DropdownMenu.Item
                  icon="descriptive"
                  label={i18n.t('components:AiEditor:descriptive')}
                  onClick={() => handleImprove(aiPromptTypes.DESCRIPTIVE)}
                />
                <DropdownMenu.Item
                  icon="proofread"
                  label={i18n.t('components:AiEditor:proofread')}
                  onClick={() => handleImprove(aiPromptTypes.PROOFREAD)}
                />
              </DropdownMenu>
            </Flex>
            <Flex flex="1" />
            {isEnabled && (
              <React.Fragment>
                <Flex flex="1" />
                <Button
                  hierarchy="tertiary"
                  label={i18n.t('global:Cancel')}
                  margin="0"
                  size="small"
                  onClick={handleCancel}
                />
                <Button
                  disabled={isLoading}
                  hierarchy="secondary"
                  icon="reset"
                  label={i18n.t('global:Retry')}
                  margin="0"
                  size="small"
                  onClick={handleRetry}
                />
                <Button
                  disabled={isLoading}
                  label={i18n.t('components:AiEditor:applyChanges')}
                  margin="0"
                  size="small"
                  disableElevation
                  onClick={handleApply}
                />
              </React.Fragment>
            )}
          </Flex>
        </StyledButtonContainer>
      </StyledFooterContainer>
      <Flex gap="10px" padding="5px 0 10px">
        {response && (
          <Typography fontSize={12} variant="span" width="100%">
            {i18n.t('components:AiEditor:hint')}
          </Typography>
        )}
        {maxCharacters && (
          <Typography align="right" width="auto">
            <MaxCharactersText charactersCount={value.length} maxCharacters={maxCharacters} />
          </Typography>
        )}
      </Flex>
      {!disableFieldError && (
        <FieldError error={error} />
      )}
    </React.Fragment>
  )
}

const enhance = compose(
  withAiService,
  connector,
)

export default enhance(AiEditor)
